import React, { forwardRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, type ViewInstance } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, Container, DisplayText, useResponsive } from '@garnish/core';

import type { AllergenTypes } from '@sg/shared-fe-features-menu';

import { HomeGenericCategory } from '../HomeCategory';
import { HomeProductCard } from '../HomeProductCard';
import { HarvestBowlImage, ClassicChickenCaesarImage, ChickenPestoParmImage } from './assets';

import type { HomeProductCardSize } from '../HomeProductCard/HomeProductCard.types';

// ─────────────────────────────────────────────────────────────────────────────

export const HomeFallbackCategories: React.ForwardRefExoticComponent<
  HomeFallbackCategoriesProps & React.RefAttributes<ViewInstance>
> = forwardRef<ViewInstance, HomeFallbackCategoriesProps>((props, ref) => {
  const {
    size = 'regular',
    dietaryPreferences,
    openMenu,
    openProductDetails,
    addProductToBag,
    trackItemSelection,
  } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Container ref={ref} style={match([styles.containerXS, styles.containerSM])}>
      <Title />

      <HomeGenericCategory size={size}>
        <HomeProductCard
          withMaxWidth
          size={size}
          isEnriched={false}
          isModifiable={false}
          categoryName="Fallbacks"
          id="harvest-bowl"
          slug="harvest-bowl"
          title={formatMessage(messages.harvestBowlTitle)}
          description={formatMessage(messages.harvestBowlDescription)}
          fallbackImage={HarvestBowlImage}
          dietaryPreferences={dietaryPreferences}
          allergenProperties={[]}
          openMenu={openMenu}
          openProductDetails={openProductDetails}
          addProductToBag={addProductToBag}
          trackItemSelection={trackItemSelection}
        />

        <HomeProductCard
          withMaxWidth
          size={size}
          isEnriched={false}
          isModifiable={false}
          categoryName="Fallbacks"
          id="classic-chicken-caesar"
          slug="classic-chicken-caesar"
          title={formatMessage(messages.classicChickenCaesarTitle)}
          description={formatMessage(messages.classicChickenCaesarDescription)}
          fallbackImage={ClassicChickenCaesarImage}
          dietaryPreferences={dietaryPreferences}
          allergenProperties={[]}
          openMenu={openMenu}
          openProductDetails={openProductDetails}
          addProductToBag={addProductToBag}
          trackItemSelection={trackItemSelection}
        />

        <HomeProductCard
          withMaxWidth
          size={size}
          isEnriched={false}
          isModifiable={false}
          categoryName="Fallbacks"
          id="chicken-pesto-parm"
          slug="chicken-pesto-parm"
          title={formatMessage(messages.chickenPestoParmTitle)}
          description={formatMessage(messages.chickenPestoParmDescription)}
          fallbackImage={ChickenPestoParmImage}
          dietaryPreferences={dietaryPreferences}
          allergenProperties={[]}
          openMenu={openMenu}
          openProductDetails={openProductDetails}
          addProductToBag={addProductToBag}
          trackItemSelection={trackItemSelection}
        />
      </HomeGenericCategory>
    </Container>
  );
});

const Title = () => {
  const { currentBreakpoint } = useResponsive();

  if (currentBreakpoint.isXS) {
    return (
      <BodyText style={styles.titleXS} sizeMatch={['18']}>
        <FormattedMessage {...messages.title} />
      </BodyText>
    );
  }

  return (
    <DisplayText style={styles.titleSM} sizeMatch={['40']}>
      <FormattedMessage {...messages.title} />
    </DisplayText>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Most Popular',
    description: 'Home | Fallback Category | Title',
  },
  harvestBowlTitle: {
    defaultMessage: 'Harvest Bowl',
    description: 'Home | Fallback Category | Harvest Bowl Title',
  },
  harvestBowlDescription: {
    defaultMessage:
      'Antibiotic-free roasted chicken, roasted sweet potatoes, apples, goat cheese, roasted almonds, wild rice, organic shredded kale and balsamic vinaigrette',
    description: 'Home | Fallback Category | Harvest Bowl Description',
  },
  classicChickenCaesarTitle: {
    defaultMessage: 'Classic Chicken Caesar',
    description: 'Home | Fallback Category | Classic Chicken Caesar Title',
  },
  classicChickenCaesarDescription: {
    defaultMessage:
      'Antibiotic-free roasted chicken, chopped romaine, garlic breadcrumbs, parmesan crisps, shaved parmesan, caesar dressing and lemon squeeze, wrapped in our new olive oil tortilla',
    description: 'Home | Fallback Category | Classic Chicken Caesar Description',
  },
  chickenPestoParmTitle: {
    defaultMessage: 'Chicken Pesto Parm',
    description: 'Home | Fallback Category | Chicken Pesto Parm Title',
  },
  chickenPestoParmDescription: {
    defaultMessage:
      'Antibiotic-free roasted chicken, spicy broccoli, tomatoes, shaved parmesan, garlic breadcrumbs, golden quinoa, organic baby spinach, sweetgreen hot sauce and pesto vinaigrette',
    description: 'Home | Fallback Category | Chicken Pesto Parm Description',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    paddingHorizontal: 0,
    gap: theme.spacing['4'],
  },
  containerSM: {
    alignItems: 'center',
    gap: theme.spacing['8'],
  },
  titleXS: {
    paddingHorizontal: theme.spacing['6'],
  },
  titleSM: {
    textAlign: 'center',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeFallbackCategoriesProps = {
  size?: HomeProductCardSize;
  dietaryPreferences: AllergenTypes;
  openMenu: () => void;
  openProductDetails: (slug: string) => void;
  addProductToBag: (id: string, title: string) => Promise<void>;
  trackItemSelection: (product: string, category: string) => void;
};
